import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { build, pool } from '@fitogest/reactive-form-builder/utils';
import {
    ReactiveFormChangedField,
    ReactiveFormLifecycleEvent,
    ReactiveFormStableValueChange,
    ReactiveFormValue,
} from '@fitogest/reactive-form-builder/interfaces';
import { BaseForm } from '../../../../base-form';
import { DashboardService } from '../../../../../service/events/dashboard.service';
import { FincasApiService } from '../../../../../service/api/fincas-api.service';
import { TareasApiService } from '../../../../../service/api/tareas-api.service';
import { SubcontratoModel } from '../../../../../models/tareas/subcontrato.model';
import { SubcontratosFormDefinition } from './form-control/form-definition';

const PREVIOUS_DATA_PATCH_FIELD = '__previous_data_patch';
const DROPDOWN_REFRESH_FIELD = '__dropdown_refresh';

@Component({
    selector: 'app-subcontratos-form',
    standalone: false,
    templateUrl: './subcontratos-form.component.html',
    styleUrls: ['./subcontratos-form.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SubcontratosFormComponent extends BaseForm<SubcontratoModel> implements OnInit {
    public readonly reactiveFormLoadingLabel = 'Preparando formulario de subcontratos...';

    public form!: SubcontratosFormDefinition;
    public override model: SubcontratoModel = {};
    public loadingData = true;
    public isReactiveFormReady = false;
    public reactiveFormLoadingError = '';
    public getRequest = this.tareasApi.subcontratos.GET;
    public initialPreviousData: SubcontratoModel | null = null;
    public reactiveFormRecordId = '';

    constructor(
        public override route: ActivatedRoute,
        public override dashboard: DashboardService,
        public fincasApi: FincasApiService,
        public tareasApi: TareasApiService,
        public override router: Router,
        private readonly cdr: ChangeDetectorRef,
    ) {
        super(
            route,
            router,
            dashboard,
            tareasApi.subcontratos.PUT,
            tareasApi.subcontratos.POST,
            'subcontratos',
            'Actualizar Subcontratos',
            'Realizar Subcontratos',
            'Duplicar Subcontratos',
        );
    }

    public async ngOnInit(): Promise<void> {
        try {
            this.loadingData = true;
            this.resetExternalDropdowns();
            this.softInit(this.getType());
            this.reactiveFormRecordId = this.resolveReactiveFormRecordId();
            const initialModel = this.initializeHostModel(this.toSubcontratoModel(this.getModel()));

            this.initialPreviousData = this.getRegisterId() ? this.toSubcontratoModel(initialModel) : null;
            await this.setupForm();
            this.form.model = this.toSubcontratoModel(this.form.model);
            this.syncModelFromForm();
            this.markReactiveFormReady();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.reactiveFormLoadingError = this.getReadableInitialisationError(error);
            this.cdr.markForCheck();
            console.error('No se ha podido preparar el formulario de subcontratos.', error);
        }
    }

    public stableValueChanges(event: ReactiveFormStableValueChange<SubcontratoModel>): void {
        const changedFieldName = String(event.changedField ?? '');

        const stableValue = this.captureStableValueChange(event.value as SubcontratoModel);

        this.form.model = this.toSubcontratoModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();

        if (changedFieldName === PREVIOUS_DATA_PATCH_FIELD || changedFieldName === DROPDOWN_REFRESH_FIELD) {
            this.refreshReactiveDropdownMappings();
            return;
        }

        if (event.origin !== 'user') {
            return;
        }

        this.handleUserValueChange(event.value, event.changedField);
    }

    public reactiveFormChanges(
        formModel: ReactiveFormValue<SubcontratoModel>,
        lastChangedFieldName: ReactiveFormChangedField<SubcontratoModel>,
    ): void {
        this.stableValueChanges({
            value: formModel,
            changedField: lastChangedFieldName,
            changedFields: [String(lastChangedFieldName ?? '')],
            origin: 'user',
        });
    }

    private handleUserValueChange(
        formModel: ReactiveFormValue<SubcontratoModel>,
        lastChangedFieldName: ReactiveFormChangedField<SubcontratoModel>,
    ): void {
        const changedFieldName = String(lastChangedFieldName ?? '');

        this.form.model = this.toSubcontratoModel({
            ...this.form.model,
            ...formModel,
        });
        this.syncModelFromForm();

        if (changedFieldName === 'id_finca') {
            this.refreshReactiveDropdownMappings();
        }
    }

    public onReactiveFormLifecycleEvent(event: ReactiveFormLifecycleEvent<SubcontratoModel>): void {
        if (event.type !== 'previousDataPatched' && event.type !== 'previousDataChanged') {
            if (event.type === 'dropdownDataResolved' || event.type === 'dropdownRefresh') {
                this.refreshReactiveDropdownMappings();
            }

            return;
        }

        const stableValue = this.captureStableValueChange(event.value as SubcontratoModel);

        this.form.model = this.toSubcontratoModel({
            ...this.form.model,
            ...stableValue,
        });
        this.syncModelFromForm();
        this.refreshReactiveDropdownMappings();
    }

    private async setupForm(): Promise<void> {
        this.form = new SubcontratosFormDefinition(this.getType(), this.model);

        await this.initFormData();
    }

    private async initFormData(): Promise<void> {
        this.loadingData = true;
        this.resetExternalDropdowns();

        try {
            await pool(this.getDropdownBuildsForConfiguredCatalogs(), () => this.refreshReactiveDropdownMappings());

            this.applyCreationDefaults();
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
        } catch (error: unknown) {
            this.loadingData = false;
            this.finalizeExternalDropdowns();
            this.refreshReactiveDropdownMappings();
            throw error;
        }
    }


    private getDropdownBuildsForConfiguredCatalogs(): ReturnType<typeof build>[] {
        const configuredCatalogs = this.getConfiguredDropdownCatalogs();
        const buildsRequest: ReturnType<typeof build>[] = [];

        if (configuredCatalogs.has('id_finca')) {
            buildsRequest.push(build(this.fincasApi.fincasGET, this.form.lists.fincas, 'nombre', false));
        }

        return buildsRequest;
    }

    private getConfiguredDropdownCatalogs(): Set<string> {
        const dropdownCatalogs = this.form.formFields.dropdownCatalogs;

        return new Set([
            ...(dropdownCatalogs?.critical ?? []),
            ...(dropdownCatalogs?.deferred ?? []),
            ...(dropdownCatalogs?.lazy ?? []),
        ]);
    }

    private applyCreationDefaults(): void {
        if (!this.form.model.id_finca && this.form.lists.fincas.values.length > 0) {
            this.form.model.id_finca = this.form.lists.fincas.values[0]?.value?.id ?? null;
            this.syncModelFromForm();
        }
    }

    private markReactiveFormReady(): void {
        this.isReactiveFormReady = true;
        this.cdr.detectChanges();
        this.finalizeExternalDropdowns();
        this.refreshReactiveDropdownMappings();
        this.cdr.markForCheck();
    }

    private getReadableInitialisationError(error: unknown): string {
        if (error instanceof Error && error.message.trim() !== '') {
            return 'No se ha podido cargar el formulario de subcontratos: ' + error.message;
        }

        return 'No se ha podido cargar el formulario de subcontratos. Revisa la conexión e inténtalo de nuevo.';
    }

    private refreshReactiveDropdownMappings(): void {
        this.formBuilderRef?.refreshExternalDropdownMappings();
    }

    private syncModelFromForm(): void {
        const normalizedModel = this.toSubcontratoModel(this.form.model);

        this.form.model = normalizedModel;
        this.updateHostModel(normalizedModel);
    }

    private toSubcontratoModel(
        sourceModel: ReactiveFormValue<SubcontratoModel> | Partial<SubcontratoModel> | Record<string, unknown>,
    ): SubcontratoModel {
        const normalizedModel: Record<string, SubcontratoModel[keyof SubcontratoModel]> = {};

        for (const [key, value] of Object.entries(sourceModel)) {
            if (value !== undefined) {
                normalizedModel[key] = value as SubcontratoModel[keyof SubcontratoModel];
            }
        }

        return normalizedModel as SubcontratoModel;
    }
}
